/**
 * @file WaiterNotificationsModal.tsx
 * @description Dialog displaying live table assistance calls, bill requests, and food pickup notices.
 */

import React from 'react';
import { X, Bell, CheckCircle2, Receipt, PhoneCall } from 'lucide-react';
import type { WaiterServiceCall } from '../../../types/waiter.types';

interface WaiterNotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  calls: WaiterServiceCall[];
  readyCount: number;
  onResolveCall: (callId: string) => Promise<void>;
}

export const WaiterNotificationsModal: React.FC<WaiterNotificationsModalProps> = ({
  isOpen,
  onClose,
  calls,
  readyCount,
  onResolveCall,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[85dvh] pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-0">
        {/* Mobile slide-up drag handle */}
        <div className="sm:hidden w-full flex justify-center pt-2.5 pb-1 bg-zinc-900/60 shrink-0">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        <div className="p-4 border-b border-zinc-800 bg-zinc-900/50 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Bell className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white uppercase">Service Alerts</h2>
              <p className="text-xs text-zinc-400">{calls.length} table call{calls.length === 1 ? '' : 's'} pending</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {readyCount > 0 && (
            <div className="p-4 rounded-2xl bg-purple-950/40 border border-purple-600 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-purple-400 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-purple-200">
                    {readyCount} Order{readyCount > 1 ? 's' : ''} Ready in Kitchen
                  </p>
                  <p className="text-xs text-purple-400 mt-0.5">Pickup from pass and deliver to guests</p>
                </div>
              </div>
            </div>
          )}

          {calls.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">
              No pending guest calls or requests right now.
            </div>
          ) : (
            calls.map(call => (
              <div
                key={call.id}
                className="p-4 rounded-2xl bg-zinc-900/80 border border-zinc-800 flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2.5 rounded-xl shrink-0 ${
                    call.type === 'bill'
                      ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                      : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  }`}>
                    {call.type === 'bill' ? <Receipt className="w-5 h-5" /> : <PhoneCall className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-black text-white truncate">TABLE {call.table_number}</p>
                    <p className="text-xs text-zinc-400 capitalize mt-0.5 truncate">
                      {call.type === 'bill' ? 'Requested Bill' : 'Requested Assistance'}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onResolveCall(call.id)}
                  className="px-4 py-2.5 min-h-[42px] rounded-xl bg-zinc-800 hover:bg-emerald-600 text-zinc-200 hover:text-white text-xs font-bold transition-colors shrink-0 active:scale-95"
                >
                  Dismiss
                </button>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 bg-zinc-900/40 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 min-h-[44px] rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-bold text-sm transition-colors active:scale-[0.98]"
          >
            Close Alerts
          </button>
        </div>
      </div>
    </div>
  );
};
